/**
 * ToolConfirmationCard — ROROS M6 (External Connectivity)
 *
 * Shown in the agent chat UI when an MCP write-tool call requires explicit
 * user approval before execution. The card:
 *   - Displays the connection name, tool name, and proposed action description.
 *   - Shows a live countdown to the expiry time.
 *   - Offers Approve / Reject buttons that POST to the user-reply endpoint.
 *   - Becomes read-only (expired state) once the expiresAt time passes.
 *   - Calls onResolved() after a successful Approve or Reject so the parent
 *     chat renderer can refresh or re-query the session messages.
 */

import React, { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { t } from '@/lib/i18n';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { showToast } from '@/components/ui/Toast';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ToolConfirmationCardProps {
  sessionId: string;
  confirmationId: string;
  toolName: string;
  connectionName: string;
  formattedDescription: string;
  expiresAt: string; // ISO date string
  onResolved?: () => void;
}

// ─── Countdown hook ───────────────────────────────────────────────────────────

function useCountdown(expiresAt: string): { label: string; isExpired: boolean } {
  const getRemaining = () => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    return diff;
  };

  const [remaining, setRemaining] = useState(() => getRemaining());

  useEffect(() => {
    if (remaining <= 0) return;

    const interval = setInterval(() => {
      const next = getRemaining();
      setRemaining(next);
      if (next <= 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
    // expiresAt is stable — only recompute when it changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt]);

  if (remaining <= 0) {
    return { label: '', isExpired: true };
  }

  const totalSeconds = Math.ceil(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const label = `${minutes}:${String(seconds).padStart(2, '0')}`;

  return { label, isExpired: false };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ToolConfirmationCard({
  sessionId,
  confirmationId,
  toolName,
  connectionName,
  formattedDescription,
  expiresAt,
  onResolved,
}: ToolConfirmationCardProps) {
  const { label: countdownLabel, isExpired } = useCountdown(expiresAt);
  const [isResolved, setIsResolved] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const handleDecision = async (approved: boolean) => {
    if (isPending || isResolved || isExpired) return;
    setIsPending(true);
    try {
      await apiFetch(`/api/v1/agent-sessions/${sessionId}/user-reply`, {
        method: 'POST',
        body: JSON.stringify({ confirmationId, approved }),
      });
      setIsResolved(true);
      onResolved?.();
    } catch (err: any) {
      showToast.error(err?.message ?? 'Failed to submit decision');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-warning-200 bg-warning-50 p-4 space-y-3 text-sm"
      data-testid="tool-confirmation-card"
      data-confirmation-id={confirmationId}
    >
      {/* Title row */}
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-warning-600 flex-shrink-0" aria-hidden />
        <span className="font-semibold text-warning-800">
          {t('roros.connections.confirmation_title')}
        </span>
      </div>

      {/* Details */}
      <dl className="space-y-1.5">
        <div className="flex gap-2">
          <dt className="text-xs font-medium text-warning-700 w-24 flex-shrink-0">
            {t('roros.connections.confirm_connection')}
          </dt>
          <dd className="text-xs text-foreground">{connectionName}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-xs font-medium text-warning-700 w-24 flex-shrink-0">
            {t('roros.connections.confirm_tool')}
          </dt>
          <dd className="text-xs text-foreground font-mono">{toolName}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-xs font-medium text-warning-700 w-24 flex-shrink-0">
            {t('roros.connections.confirm_action')}
          </dt>
          <dd className="text-xs text-foreground whitespace-pre-wrap">{formattedDescription}</dd>
        </div>
      </dl>

      {/* Footer: expiry + action buttons */}
      <div className="flex items-center justify-between pt-1 border-t border-warning-200">
        {/* Expiry / resolved state */}
        <div>
          {isResolved ? (
            <span className="text-xs text-success-700 font-medium">
              {t('roros.connections.confirm_resolved')}
            </span>
          ) : isExpired ? (
            <span className="text-xs text-error-600 font-medium">
              {t('roros.connections.confirm_expired')}
            </span>
          ) : (
            <span className="text-xs text-warning-700">
              {t('roros.connections.confirm_expires')} {countdownLabel}
            </span>
          )}
        </div>

        {/* Action buttons — hidden once resolved or expired */}
        {!isResolved && !isExpired && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDecision(false)}
              isLoading={isPending}
              disabled={isPending}
              data-testid="tool-confirmation-reject"
            >
              {t('roros.connections.confirm_reject')}
            </Button>
            <Button
              size="sm"
              variant="success"
              onClick={() => handleDecision(true)}
              isLoading={isPending}
              disabled={isPending}
              data-testid="tool-confirmation-approve"
            >
              {t('roros.connections.confirm_approve')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
